// Crisp mirrored-peak waveform drawn on DPR-scaled canvas tiles, so long
// clips stay sharp at any length (no stretched bitmaps / SVG blocks).
import React, { memo, useEffect, useRef } from "react";
import { clipEnvelopeAt } from "../../lib/studioEngine";

const TILE_PX = 512;
const BAR_STEP = 2; // CSS px per bar (1px bar + 1px gap)

function drawTile(canvas, o) {
  const dpr = Math.min(3, window.devicePixelRatio || 1);
  const w = o.tileW;
  const h = o.height;
  canvas.width = Math.ceil(w * dpr);
  canvas.height = Math.ceil(h * dpr);
  const g = canvas.getContext("2d");
  if (!g) return;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  g.clearRect(0, 0, w, h);
  const mid = h / 2;
  const amp = mid - 2;
  const gain = Math.pow(10, (o.gainDb || 0) / 20);
  const win = o.srcEndMs - o.srcStartMs;
  const msPerPx = 1000 / o.pxPerSec;
  const n = o.peaks.length;
  const hasFades = o.fadeInMs > 0 || o.fadeOutMs > 0;
  g.fillStyle = o.color;
  for (let x = 0; x < w; x += BAR_STEP) {
    const u = (o.x0 + x) * msPerPx;
    const a = (o.srcStartMs + u) / 1000 * o.rate;
    const b = (o.srcStartMs + u + BAR_STEP * msPerPx) / 1000 * o.rate;
    let i0 = Math.floor(a);
    const i1 = Math.max(i0 + 1, Math.ceil(b));
    if (i0 >= n) break;
    let mx = 0;
    for (; i0 < i1 && i0 < n; i0++) if (o.peaks[i0] > mx) mx = o.peaks[i0];
    const env = hasFades ? clipEnvelopeAt(u, win, o.fadeInMs, o.fadeOutMs) : 1;
    const v = Math.min(1, (mx / 255) * gain * env);
    const bh = Math.max(0.75, v * amp);
    g.fillStyle = v >= 0.99 ? "#f87171" : o.color;
    g.fillRect(x, mid - bh, Math.max(1, BAR_STEP - 1), bh * 2);
  }
  if (hasFades && o.showFades) {
    g.strokeStyle = "rgba(255,255,255,0.9)";
    g.lineWidth = 1.25;
    g.beginPath();
    for (let x = 0; x <= w; x += 2) {
      const u = (o.x0 + x) * msPerPx;
      const y = mid - clipEnvelopeAt(u, win, o.fadeInMs, o.fadeOutMs) * amp;
      if (x === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke();
  }
}

const Tile = memo(function Tile(props) {
  const ref = useRef(null);
  const { tileW, height, x0, version, color, gainDb, fadeInMs, fadeOutMs, srcStartMs, srcEndMs, showFades, pxPerSec } = props;
  useEffect(() => {
    if (ref.current) drawTile(ref.current, props);
  }, [tileW, height, x0, version, color, gainDb, fadeInMs, fadeOutMs, srcStartMs, srcEndMs, showFades, pxPerSec]); // eslint-disable-line
  return (
    <canvas ref={ref} className="absolute top-0 pointer-events-none"
      style={{ left: x0, width: tileW, height }} />
  );
});

export const WaveformCanvas = memo(function WaveformCanvas({
  peaks = [], rate = 20, srcStartMs = 0, srcEndMs = 0, pxPerSec, height = 48,
  color = "#d946ef", gainDb = 0, fadeInMs = 0, fadeOutMs = 0, showFades = false, version, testid = "wave-canvas",
}) {
  const width = Math.max(1, Math.round(((srcEndMs - srcStartMs) / 1000) * pxPerSec));
  const tiles = [];
  for (let x0 = 0; x0 < width; x0 += TILE_PX) {
    const tileW = Math.min(TILE_PX, width - x0);
    const tileEndIdx = Math.ceil(((srcStartMs + ((x0 + tileW) / pxPerSec) * 1000) / 1000) * rate);
    tiles.push({ x0, tileW, v: `${version ?? peaks.length}:${Math.min(peaks.length, tileEndIdx)}` });
  }
  return (
    <div className="absolute inset-y-0 left-0" style={{ width, height }} data-testid={testid}
         data-peaks={peaks.length} data-rate={rate}>
      {tiles.map((t) => (
        <Tile key={t.x0} x0={t.x0} tileW={t.tileW} version={t.v} height={height} peaks={peaks} rate={rate}
          srcStartMs={srcStartMs} srcEndMs={srcEndMs} pxPerSec={pxPerSec} color={color}
          gainDb={gainDb} fadeInMs={fadeInMs} fadeOutMs={fadeOutMs} showFades={showFades} />
      ))}
    </div>
  );
});
